import React from "react";
import { useTranslation } from "react-i18next";

import { DoubleArrowIcon, WorldIcon } from "../Icons";
import { Typo } from "../Typo";

function LanguagesOverlay({ languages }: { languages: string[] }) {
  return (
    <div className="invisible absolute left-0 top-full z-10 mt-2 w-64 rounded-lg border border-stroke-primary bg-surface-primary p-4 opacity-0 shadow-[0px_4px_16px_rgba(3,20,46,0.05)] transition-opacity duration-150 group-hover:visible group-hover:opacity-100">
      <div className="columns-2 gap-x-3 [&>*]:mb-2">
        {languages.map((lang) => (
          <Typo key={lang} element="p" type="bodySM">
            {lang}
          </Typo>
        ))}
      </div>
    </div>
  );
}

function SizesOverlay({ sizes }: { sizes: string[] }) {
  return (
    <div className="invisible absolute left-0 top-full z-10 mt-2 w-[200px] rounded-lg border border-stroke-primary bg-surface-primary p-4 opacity-0 shadow-[0px_4px_16px_rgba(3,20,46,0.05)] transition-opacity duration-150 group-hover:visible group-hover:opacity-100">
      <div className="w-full columns-2 gap-x-3 [&>*]:mb-2">
        {sizes.map((size) => (
          <Typo key={size} element="p" type="bodySM">
            {size}
          </Typo>
        ))}
      </div>
    </div>
  );
}

function MetaItem({
  icon,
  label,
  overlay,
  onHoverOpen,
}: {
  icon: React.ReactNode;
  label: string;
  overlay: React.ReactNode;
  /** Fired the first time the hover overlay for this item becomes visible. */
  onHoverOpen?: () => void;
}) {
  return (
    <div className="group relative" onMouseEnter={onHoverOpen}>
      <div className="flex items-center gap-2 text-text-tertiary">
        {icon}
        <Typo
          element="span"
          type="bodySM"
          textColor="text-text-tertiary"
          className="underline"
        >
          {label}
        </Typo>
      </div>
      {overlay}
    </div>
  );
}

function TagBadge({ tag }: { tag: string }) {
  return (
    <div className="inline-flex min-h-5 items-center rounded-full bg-surface-accent-blue-subtler px-[6px] lg:min-h-6">
      <Typo
        element="span"
        type="labelXS"
        smType="label2XS"
        className="flex items-center"
      >
        {tag}
      </Typo>
    </div>
  );
}

type CardMetaProps = {
  title: string;
  languages: string[];
  sizes: string[];
  tags: string[];
  /** Fired when the language list's hover tooltip opens. */
  onLanguagesTooltipOpen?: () => void;
  /** Fired when the size list's hover tooltip opens. */
  onSizesTooltipOpen?: () => void;
};

export function CardMeta({
  title,
  languages,
  sizes,
  tags,
  onLanguagesTooltipOpen,
  onSizesTooltipOpen,
}: CardMetaProps) {
  const { t } = useTranslation();

  return (
    <div className="space-y-2">
      <div className="flex items-start justify-between gap-3">
        <Typo element="h3" type="labelMD" className="flex-1">
          {title}
        </Typo>
        <div className="flex shrink-0 items-center gap-2">
          {tags.map((tag) => (
            <TagBadge
              key={tag}
              tag={t(`bannerLibrary_filter_${tag.toLowerCase()}`, tag)}
            />
          ))}
        </div>
      </div>

      <div className="flex items-center gap-3">
        <MetaItem
          icon={<WorldIcon className="size-4" />}
          label={t("marketingCard_languages", "{{count}} languages", {
            count: languages.length,
          })}
          overlay={<LanguagesOverlay languages={languages} />}
          onHoverOpen={onLanguagesTooltipOpen}
        />

        <div className="h-6 w-px bg-stroke-primary" />

        <MetaItem
          icon={<DoubleArrowIcon className="size-4" />}
          label={t("marketingCard_sizes", "{{count}} sizes", {
            count: sizes.length,
          })}
          overlay={<SizesOverlay sizes={sizes} />}
          onHoverOpen={onSizesTooltipOpen}
        />
      </div>
    </div>
  );
}
